<template>
  <Card class="card">
    <View direction="column" gap="md">
      <View v-if="bundleAccepted || bundleDeclined" justify="center">
        <IconTile
          size="medium"
          shape="rounded"
          :color="bundleAccepted ? 'green' : 'red'"
          :icon="bundleAccepted ? 'file-check-02' : 'file-x-02'"
          :colored-icon="true"
        />
      </View>

      <View direction="column" gap="sm">
        <Text variant="h2" :align="canDecide ? 'start' : 'center'">
          {{ bundleTexts.header }}
        </Text>
        <Text variant="body-medium" color="secondary" :align="canDecide ? 'start' : 'center'">
          {{ bundleTexts.body }}
        </Text>
      </View>

      <Hint v-if="bundleUpdateRequested && bundle?.latestUpdateRequest" icon="message-chat-circle" type="info">
        “{{ bundle.latestUpdateRequest.message }}”
      </Hint>

      <View direction="column" gap="md">
        <template v-if="canDecide">
          <Button
            :data-manage-action="bundle?.property.id"
            @click="acceptApplicationModalRef?.modalRef?.show()"
            :appearance="'primary'"
          >
            Accept application
          </Button>
          <Button
            v-if="bundleSubmitted"
            :data-manage-action="bundle?.property.id"
            @click="requestApplicationUpdateModalRef?.modalRef?.show()"
            :variant="'secondary'"
          >
            Request more info
          </Button>
          <Button
            :data-manage-action="bundle?.property.id"
            @click="declineApplicationModalRef?.modalRef?.show()"
            :appearance="'danger'"
            :variant="'secondary'"
          >
            Decline application
          </Button>
        </template>

        <template v-if="bundleDeclined || bundleAccepted">
          <Card variant="outline" size="medium" class="contact-card">
            <View direction="column" align="center">
              <Text variant="label-large" v-if="renter?.user.phoneNumber">
                <a :href="`tel:${renter?.user.phoneNumber}`">{{ renter?.user.phoneNumber }}</a>
              </Text>
              <Text variant="body-large" color="secondary" v-if="renter?.user.email">
                <a :href="`mailto:${renter?.user.email}`">{{ renter?.user.email }}</a>
              </Text>
            </View>
          </Card>
        </template>
      </View>
    </View>

    <AcceptRenterApplicationModal
      :bundle-id="bundleId"
      :renter-first-name="renterFirstName || ''"
      ref="acceptApplicationModalRef"
    />
    <RequestApplicationUpdateModal :bundle-id="bundleId" ref="requestApplicationUpdateModalRef" />
    <DeclineRenterApplicationModal :bundle-id="bundleId" ref="declineApplicationModalRef" />
  </Card>
</template>

<script setup lang="ts">
import { format } from 'date-fns';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { ApplicationBundleStatus } from '@tallo/applications';
import { Button, Card, Hint, IconTile, Text, View } from '@tallo/design-system';

import { useRenterProfileStore } from '../store/renter-profile.store';
import AcceptRenterApplicationModal from './accept-renter-application-modal.vue';
import DeclineRenterApplicationModal from './decline-renter-application-modal.vue';
import RequestApplicationUpdateModal from './request-application-update-modal.vue';

const acceptApplicationModalRef = ref<InstanceType<typeof AcceptRenterApplicationModal>>();
const declineApplicationModalRef = ref<InstanceType<typeof DeclineRenterApplicationModal>>();
const requestApplicationUpdateModalRef = ref<InstanceType<typeof RequestApplicationUpdateModal>>();

const renterProfileStore = useRenterProfileStore();
const route = useRoute();

const bundleId = ref(route.params.applicationId as string);

const renter = computed(() => renterProfileStore.getRenterProfile);
const renterFirstName = computed(() => renter.value?.user.name.split(' ')[0]);

const bundle = computed(() => renterProfileStore.bundles.bundles.find((bundle) => bundle.id === bundleId.value));
const requestedOn = computed(() => {
  const requestedAt = bundle.value?.latestUpdateRequest?.requestedAt;

  return requestedAt ? ` on ${format(new Date(requestedAt), 'MMM d')}` : '';
});
const bundleTexts = computed(() => {
  switch (bundle.value?.status) {
    case ApplicationBundleStatus.ACCEPTED:
      return {
        header: 'Application accepted',
        body: 'Get your property rented faster by contacting your future renters',
      };
    case ApplicationBundleStatus.DECLINED:
      return {
        header: 'Application declined',
        body: 'You can still reach out to the renter if you changed your mind',
      };
    case ApplicationBundleStatus.UPDATE_REQUESTED:
      return {
        header: 'Waiting for the renter',
        body: `You asked for more information${requestedOn.value}. You can still accept or decline in the meantime.`,
      };
    default:
      return {
        header: 'Meets your criteria?',
        body: 'Renters will be notified of your decision. Whether you accept or reject the application, Tallo will guide the renters through what comes next.',
      };
  }
});
const bundleSubmitted = computed(() => bundle.value?.status === ApplicationBundleStatus.SUBMITTED);
const bundleUpdateRequested = computed(() => bundle.value?.status === ApplicationBundleStatus.UPDATE_REQUESTED);
const bundleAccepted = computed(() => bundle.value?.status === ApplicationBundleStatus.ACCEPTED);
const bundleDeclined = computed(() => bundle.value?.status === ApplicationBundleStatus.DECLINED);
/** An open info request does not take the verdict away — the owner can still accept or decline. */
const canDecide = computed(() => bundleSubmitted.value || bundleUpdateRequested.value);
</script>

<style scoped lang="scss">
@import 'styles/mixins/layout.mixins';

.card {
  height: fit-content;
}

.contact-card.contact-card {
  background-color: var(--t-color-neutral-50);
}

@include screen-min-lg {
  .card {
    width: 100%;
    max-width: 23.75rem;
  }
}
</style>
